import {z} from 'zod';
import {FirestoreTimestampSchema} from '../types';
import type {UniqueStringId} from '../../types/branded';

// ============================================================================
// Email Batch Document Types
// ============================================================================

export type EmailBatchId = UniqueStringId<'EmailBatchId'>;

/**
 * Base schema for email batch documents. Uses FirestoreTimestampSchema so
 * SDK-specific modules (admin/, client/) can extend it with their own
 * timestamp transforms.
 */
export const EmailBatchDocumentSchema = z
  .object({
    /** Timestamp when the batch metadata document was created. */
    createdAt: FirestoreTimestampSchema,
    /** @deprecated Present on old documents. Replaced by requestedRecordCount
     * and requestedTemplatesToEmails to avoid exceeding Firestore's 1 MB
     * document size limit. */
    requestedRecords: z
      .array(
        z.object({
          to: z.union([z.string(), z.array(z.string())]),
          template: z.object({
            name: z.string(),
            data: z.record(z.string(), z.unknown()).optional(),
          }),
        }),
      )
      .optional(),
    /** Number of records requested for this batch invocation. */
    requestedRecordCount: z.number().optional(),
    /** Map of template id -> recipients requested for that template. */
    requestedTemplatesToEmails: z
      .record(z.string(), z.array(z.string()))
      .optional(),
    /** List of records (firestore documents path for mail document) that were successfully sent. */
    sentRecords: z.array(z.unknown()),
    /** Best-effort list of recipients that were requested but not queued. */
    unsentRecipients: z.array(z.string()).optional(),
  })
  .loose();

export type EmailBatchDocument = z.infer<typeof EmailBatchDocumentSchema>;

// ============================================================================
// Paths
// ============================================================================

export const EMAIL_BATCH_COLLECTION = 'email-batches' as const;

/**
 * Collection path for batch metadata: `email-batches/{courseId}/docs`.
 * Individual batches are **not** top-level `email-batches/{batchId}`.
 */
export function getEmailBatchCollectionPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `${EMAIL_BATCH_COLLECTION}/${courseId}/docs`;
}

export function getEmailBatchDocumentPath({
  courseId,
  batchId,
}: {
  courseId: string;
  batchId: string;
}): string {
  return `${getEmailBatchCollectionPath({courseId})}/${batchId}`;
}
